import { Controller } from "@hotwired/stimulus";

// Modal de selección de posts de Instagram para AutoDM.
//
// Mantiene un Set interno de IDs seleccionados (inicializado desde
// `data-manage--autodm--posts-modal-selected-value` que viene del backend
// con el array de selected_ids actuales del form).
//
// Al hacer click en un post (data-action="click->...#toggle"), agrega/quita
// del Set y actualiza visualmente (border + check icon).
//
// Al confirmar (botón "Confirmar selección" o "Activo en N posts"), despacha
// un evento custom `autodm:posts-confirmed` con `detail.ids = Array<string>`.
// El controller `manage--autodm--form` escucha ese evento y actualiza los
// hidden inputs `autodm_form[post_ids][]` del form padre. Después cierra el
// modal via el modal controller (Escape simulation no funciona; usamos cierre
// del frame).

export default class extends Controller {
  static targets = ["post", "check", "confirmBtn", "confirmLabel"];
  static values = {
    selected: { type: Array, default: [] }
  };

  connect() {
    // Normalizo a Set<String> para toggles O(1).
    this._selected = new Set((this.selectedValue || []).map(String));
    this._refreshConfirmLabel();
  }

  toggle(event) {
    event.preventDefault();
    const btn = event.currentTarget;
    const id = btn.dataset.postId;
    if (!id) return;

    if (this._selected.has(id)) {
      this._selected.delete(id);
      btn.classList.remove("border-[#0477f8]");
      btn.classList.add("border-transparent");
      const check = btn.querySelector("[data-manage--autodm--posts-modal-target='check']");
      if (check) {
        check.classList.add("hidden");
        check.classList.remove("flex");
      }
    } else {
      this._selected.add(id);
      btn.classList.add("border-[#0477f8]");
      btn.classList.remove("border-transparent");
      const check = btn.querySelector("[data-manage--autodm--posts-modal-target='check']");
      if (check) {
        check.classList.remove("hidden");
        check.classList.add("flex");
      }
    }
    this._refreshConfirmLabel();
  }

  clear(event) {
    event.preventDefault();
    this._selected.clear();
    this.postTargets.forEach((btn) => {
      btn.classList.remove("border-[#0477f8]");
      btn.classList.add("border-transparent");
      const check = btn.querySelector("[data-manage--autodm--posts-modal-target='check']");
      if (check) {
        check.classList.add("hidden");
        check.classList.remove("flex");
      }
    });
    this._refreshConfirmLabel();
  }

  confirm(event) {
    event.preventDefault();
    const ids = Array.from(this._selected);
    document.dispatchEvent(new CustomEvent("autodm:posts-confirmed", { detail: { ids } }));
    this._closeModal();
  }

  _refreshConfirmLabel() {
    if (!this.hasConfirmLabelTarget) return;
    const n = this._selected.size;
    const ds = this.element.dataset;
    const labelAll  = ds.labelAllPosts     || "Habilitado para todos los posts";
    const labelOne  = ds.labelActiveInPost || "Activo en %{count} post";
    const labelMany = ds.labelActiveInPosts || "Activo en %{count} posts";
    if (n === 0) {
      this.confirmLabelTarget.textContent = labelAll;
    } else if (n === 1) {
      this.confirmLabelTarget.textContent = labelOne.replace("%{count}", n);
    } else {
      this.confirmLabelTarget.textContent = labelMany.replace("%{count}", n).replace("0", n);
    }
  }

  _closeModal() {
    // El modal controller vive en el ancestro (data-controller="manage--modal").
    // Usamos su instancia via Stimulus `application` para invocar close().
    const modalEl = this.element.closest("[data-controller~='manage--modal']");
    if (!modalEl) return;
    const app = window.Stimulus || (this.application);
    if (app && app.getControllerForElementAndIdentifier) {
      const modalCtrl = app.getControllerForElementAndIdentifier(modalEl, "manage--modal");
      if (modalCtrl) {
        modalCtrl.close();
        return;
      }
    }
    // Fallback: vacía el frame global #modal.
    const frame = document.querySelector("turbo-frame#modal");
    if (frame) frame.innerHTML = "";
  }
}
